import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarIcon, UserIcon, PinIcon, StarIcon, ShieldIcon, CheckBadgeIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, SearchIcon, ArrowRightIcon, FlexIcon, PriceTagIcon } from "../site/icons.jsx";
import { typeLabel, lodgingLabel, lodgingMicrodataTypes } from "../lib/hotelTypes.js";
import { readOffer, offerAsRoomType } from "../lib/rateCarry.js";
import { computeRate, priceBreakdown } from "../lib/rateMath.js";
import { lodgingNode, stayOfferNode, breadcrumbNode, useJsonLd, useHead, propertyPath, islandSlugFor, islandDisplayFor, clip, CONTEXT } from "../lib/seo.js";
import { NAV, OFFER_TYPE, COMPOUND_TYPE, PriceComponent, ReturnPolicy, SoldOutOffer, stayDateTime, cancelDateTime, money2 } from "../site/HotelMicrodata.jsx";

const IMAGE_ROOT = "https://images.aloharates.com";
const BOARD_LABEL = { RO: "No extras", BI: "Breakfast included", HB: "Half board", FB: "Full board", AI: "All inclusive" };
const REVIEW_FILTERS = ["All", "Couples", "Solo", "Families", "Groups"];
const REVIEW_FILTER_KEYS = { Couples:["couple"], Solo:["solo"], Families:["family"], Groups:["group"] };

const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const parseISO = v => v ? new Date(`${v}T12:00:00`) : null;
const fmtShort = v => { const d = parseISO(v); return d ? d.toLocaleDateString("en-US",{month:"short",day:"numeric"}) : ""; };
const fmtLong  = v => { const d = parseISO(v); return d ? d.toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"}) : ""; };
const nights   = (a, b) => a && b ? Math.max(1, Math.round((parseISO(b) - parseISO(a)) / 86400000)) : 0;
const slugify  = (v = "") => v.normalize("NFD").replace(/[̀-ͯ]/g,"").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
const ratingLabel = v => { const n = Number(v); if (!Number.isFinite(n)) return ""; if (n >= 9) return "Excellent"; if (n >= 8) return "Very Good"; if (n >= 7) return "Good"; if (n >= 4) return "Fair"; return "Poor"; };
const scoreColor  = v => { const n = Number(v); if (n >= 8) return "#0e5b71"; if (n >= 6) return "#3d95b6"; if (n >= 4) return "#f4691e"; return "#ef4444"; };

/* ─── PhotoMosaic ────────────────────────────────────────────────────────── */

function usePhotoProbe(propertyId, manifest) {
  const [photos, setPhotos] = useState([]);
  const [probed, setProbed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!propertyId) return;
    setPhotos([]);
    setProbed(false);
    const known = manifest?.[propertyId]?.propertyLive;
    const indexes = Array.isArray(known) && known.length ? known : Array.from({ length: 40 }, (_, i) => i);
    const probes = indexes.map(i => new Promise(resolve => {
      const url = `${IMAGE_ROOT}/properties/${propertyId}/${i}.webp`;
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    }));
    Promise.all(probes).then(results => {
      if (cancelled) return;
      setPhotos(results.filter(Boolean));
      setProbed(true);
    });
    return () => { cancelled = true; };
  }, [propertyId, manifest]);

  return { photos, probed };
}

function PhotoMosaic({ propertyId, propertyName, manifest, onShowAll }) {
  const { photos, probed } = usePhotoProbe(propertyId, manifest);

  if (!probed) return (
    <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[480px] rounded-3xl overflow-hidden">
      <div className="col-span-2 row-span-2 bg-sand-200 animate-pulse"/>
      {[0,1,2,3].map(i => <div key={i} className="bg-sand-200 animate-pulse"/>)}
    </div>
  );

  if (photos.length === 0) return (
    <div className="grid place-items-center h-[480px] rounded-3xl bg-sand-100 text-ink-muted">Photos coming soon</div>
  );

  const main = photos[0];
  const thumbs = photos.slice(1, 5);

  return (
    <div className="relative w-full">
      <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-2 md:h-[520px] rounded-3xl overflow-hidden">
        <button type="button" onClick={() => onShowAll(0)} className="relative md:col-span-2 md:row-span-2 aspect-[4/3] md:aspect-auto group overflow-hidden">
          <img src={main} alt={propertyName} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"/>
        </button>
        {thumbs.map((src, i) => (
          <button key={src} type="button" onClick={() => onShowAll(i + 1)} className="relative hidden md:block overflow-hidden group">
            <img src={src} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"/>
          </button>
        ))}
      </div>
      <button type="button" onClick={() => onShowAll(-1)} className="absolute right-4 bottom-4 btn-secondary py-2 px-4 text-sm shadow-cardHover z-10">
        <span className="inline-block h-4 w-4 mr-1"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg></span>
        Show all {photos.length} photos
      </button>
    </div>
  );
}

function PhotoLightbox({ photos, index, onClose, propertyName }) {
  // index === -1 → open in grid mode; otherwise open in single-view at that index
  const [mode, setMode] = useState(index === -1 ? "grid" : "single");
  const [i, setI] = useState(index === -1 ? 0 : index);
  useEffect(() => {
    setMode(index === -1 ? "grid" : "single");
    setI(index === -1 ? 0 : index);
  }, [index]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => {
      if (e.key === "Escape") { if (mode === "single" && index === -1) setMode("grid"); else onClose(); }
      if (mode === "single") {
        if (e.key === "ArrowLeft") setI(x => (x - 1 + photos.length) % photos.length);
        if (e.key === "ArrowRight") setI(x => (x + 1) % photos.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [photos.length, onClose, mode, index]);

  const openSingle = idx => { setI(idx); setMode("single"); };
  const backToGrid = () => setMode("grid");
  const canReturnToGrid = index === -1;

  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-ink/10 bg-white">
        {mode === "single" && canReturnToGrid ? (
          <button onClick={backToGrid} className="text-sm font-medium text-ink hover:text-ocean-700 flex items-center gap-1.5">
            <ChevronLeftIcon size={16}/> Back to gallery
          </button>
        ) : (
          <span className="font-display text-lg text-ink truncate">{propertyName}</span>
        )}
        <button onClick={onClose} className="h-9 w-9 rounded-full border border-ink/15 hover:bg-sand-100 flex items-center justify-center" aria-label="Close"><CloseIcon size={16}/></button>
      </div>

      {mode === "grid" ? (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto p-4 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {photos.map((src, idx) => (
                <button key={src} onClick={() => openSingle(idx)} className="relative aspect-[4/3] overflow-hidden rounded-xl bg-sand-100 group">
                  <img src={src} alt="" loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"/>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 flex items-center justify-center relative bg-sand-50">
            <button onClick={() => setI(x => (x - 1 + photos.length) % photos.length)} className="absolute left-4 md:left-8 h-11 w-11 rounded-full bg-white/90 hover:bg-white shadow-cardHover text-ink flex items-center justify-center" aria-label="Previous"><ChevronLeftIcon size={20}/></button>
            <img src={photos[i]} alt="" className="max-h-[80vh] max-w-[90vw] object-contain"/>
            <button onClick={() => setI(x => (x + 1) % photos.length)} className="absolute right-4 md:right-8 h-11 w-11 rounded-full bg-white/90 hover:bg-white shadow-cardHover text-ink flex items-center justify-center" aria-label="Next"><ChevronRightIcon size={20}/></button>
          </div>
          <div className="text-center text-xs text-ink-muted py-2">{i + 1} / {photos.length}</div>
          <div className="px-4 pb-4 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max mx-auto">
              {photos.map((src, idx) => (
                <button key={src} onClick={() => setI(idx)} className={`h-14 w-20 shrink-0 overflow-hidden rounded-md ring-2 transition ${idx === i ? "ring-ocean-600" : "ring-transparent opacity-60 hover:opacity-100"}`}>
                  <img src={src} alt="" className="h-full w-full object-cover"/>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Range calendar (reused shape from HeroSearch) ─────────────────────── */

// Only as many week rows as the month needs — a fixed six left a blank row
// under most months. Matches HeroSearch's calendar.
const monthDays = month => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Math.ceil((first.getDay() + daysInMonth) / 7) * 7;
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  return Array.from({ length: cells }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
};

function CalendarMonth({ month, today, start, end, phase, hover, onHover, onPick }) {
  return (
    <div className="w-full">
      <h4 className="text-center font-semibold text-sm text-ink mb-2">{month.toLocaleDateString("en-US",{month:"long",year:"numeric"})}</h4>
      <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => <span key={d}>{d}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {monthDays(month).map(day => {
          const val = iso(day);
          const outside = day.getMonth() !== month.getMonth();
          const past = day < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          const previewEnd = !end && phase === "end" && hover > start ? hover : "";
          const rangeEnd = end || previewEnd;
          const inRange = !!start && !!rangeEnd && val > start && val < rangeEnd;
          const isPreview = val === previewEnd;
          const isStart = val === start;
          const isEnd = val === end || isPreview;
          const disabled = past || outside;
          let classes = "relative aspect-square flex items-center justify-center text-sm rounded-full font-medium select-none";
          if (disabled) classes += " text-ink-muted/40 cursor-not-allowed";
          else classes += " cursor-pointer hover:bg-ocean-50 text-ink";
          if (outside) classes += " invisible";
          if (inRange) classes += " !rounded-none bg-ocean-100/70 text-ocean-800";
          if (isStart || isEnd) classes += " bg-ocean-600 text-white hover:bg-ocean-700 z-10";
          if (isStart && rangeEnd) classes += " !rounded-l-full !rounded-r-none";
          if (isEnd && start && val !== start) classes += " !rounded-r-full !rounded-l-none";
          if (isStart && isEnd) classes += " !rounded-full";
          return <button type="button" key={val} disabled={disabled} className={classes} onClick={() => onPick(val)} onMouseEnter={() => onHover(val)} onMouseLeave={() => onHover("")}>{day.getDate()}</button>;
        })}
      </div>
    </div>
  );
}

/* ─── Room image (probes R2 by mappedRoomId) ─────────────────────────────── */

// Shared cache: one /api/hotel-data fetch per property, shared across every
// RoomImage on the page. Value is a Promise that resolves once with
// { roomsById: Map<String(roomId), Array<url>> }.
const hotelDetailsCache = new Map();
function fetchHotelDetails(propertyId) {
  if (hotelDetailsCache.has(propertyId)) return hotelDetailsCache.get(propertyId);
  const p = fetch(`/api/data/hotel?hotelId=${encodeURIComponent(propertyId)}`)
    .then(r => r.ok ? r.json() : null)
    .then(payload => {
      const data = payload?.data || payload || {};
      const rooms = data.rooms || [];
      const roomsById = new Map();
      const roomDescriptionsById = new Map();
      for (const room of rooms) {
        const urls = (room?.photos || []).map(ph => ph?.hd_url || ph?.url).filter(Boolean);
        if (room?.id != null) {
          roomsById.set(String(room.id), urls);
          const desc = room?.roomDescription || room?.description || "";
          if (desc) roomDescriptionsById.set(String(room.id), desc);
        }
      }
      return {
        roomsById,
        roomDescriptionsById,
        description: data.hotelDescription || data.description || ""
      };
    })
    .catch(() => ({ roomsById: new Map(), roomDescriptionsById: new Map(), description: "" }));
  hotelDetailsCache.set(propertyId, p);
  return p;
}

// Which catalog room does a rate belong to? The rate's mappedRoomId when LiteAPI
// supplied one; otherwise, if the property has exactly one catalog room (the
// normal shape for a vacation rental priced with roomMapping off), that room.
// Returns "" when there is nothing to resolve to.
function resolveRoomKey(roomsById, mappedRoomId) {
  if (mappedRoomId) return String(mappedRoomId);
  if (roomsById && roomsById.size === 1) return [...roomsById.keys()][0];
  return "";
}

function AboutSection({ property }) {
  const local = property.description || property.hotelDescription || "";
  const [remote, setRemote] = useState("");
  const [loading, setLoading] = useState(!local);

  useEffect(() => {
    if (local) return;
    let cancelled = false;
    setLoading(true);
    fetchHotelDetails(property.id).then(({ description }) => {
      if (cancelled) return;
      setRemote(description || "");
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [property.id, local]);

  const body = local || remote;
  if (!loading && !body) return null;
  return (
    <section>
      <h2 className="section-title">About this property</h2>
      {loading ? (
        <div className="mt-4 space-y-2">
          <div className="h-3 w-full bg-sand-200 rounded animate-pulse"/>
          <div className="h-3 w-11/12 bg-sand-200 rounded animate-pulse"/>
          <div className="h-3 w-4/5 bg-sand-200 rounded animate-pulse"/>
        </div>
      ) : (
        <div className="prose-body mt-4 [&_p]:mb-3 [&_strong]:text-ink [&_b]:text-ink" dangerouslySetInnerHTML={{ __html: body }}/>
      )}
    </section>
  );
}

// onResolved({ photos, remoteDescription }) fires once the photo sources have
// been exhausted, so the card can decide whether "More details" has anything
// to show.
function RoomImage({ propertyId, mappedRoomId, alt, onResolved }) {
  const [urls, setUrls] = useState([]);
  const [probed, setProbed] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setUrls([]);
    setProbed(false);
    setI(0);
    const finish = (list, remoteDescription = true) => {
      if (cancelled) return;
      setUrls(list);
      setProbed(true);
      onResolved?.({ photos: list.length, remoteDescription });
    };

    // Second-chance source: the hotel data endpoint. Looks the room up by id;
    // when the rate carried no mappedRoomId (rentals priced with roomMapping
    // off) and the property has exactly one catalog room, that room is it.
    const fromDataEndpoint = async (roomId) => {
      const { roomsById, roomDescriptionsById } = await fetchHotelDetails(propertyId);
      if (cancelled) return;
      const key = resolveRoomKey(roomsById, roomId);
      const list = key ? (roomsById.get(key) || []) : [];
      console.log("[RoomImage] fallback via /api/data/hotel — mappedRoomId=", roomId, "resolvedKey=", key, "remotePhotos=", list.length, "keysAvailable=", [...roomsById.keys()].slice(0, 8));
      finish(list, !!(key && roomDescriptionsById.get(key)));
    };

    if (!mappedRoomId) { fromDataEndpoint(null); return () => { cancelled = true; }; }

    const probes = Array.from({ length: 8 }, (_, n) => new Promise(resolve => {
      const url = `${IMAGE_ROOT}/rooms/${propertyId}/${mappedRoomId}/${n}.webp`;
      const img = new Image();
      img.onload = () => resolve(url);
      img.onerror = () => resolve(null);
      img.src = url;
    }));
    Promise.all(probes).then(results => {
      if (cancelled) return;
      const r2Urls = results.filter(Boolean);
      console.log("[RoomImage]", propertyId, "mappedRoomId=", mappedRoomId, "r2Photos=", r2Urls.length);
      if (r2Urls.length) { finish(r2Urls); return; }
      fromDataEndpoint(mappedRoomId);
    });
    return () => { cancelled = true; };
  }, [propertyId, mappedRoomId]);

  if (!probed) return <div className="w-full md:w-[220px] aspect-[4/3] bg-sand-200 animate-pulse rounded-2xl shrink-0"/>;
  // R2 and the data endpoint both came up empty: render nothing at all so the
  // card text starts at the left edge instead of beside an empty placeholder.
  if (!urls.length) return null;
  const url = urls[i];
  return (
    <div className="relative w-full md:w-[220px] aspect-[4/3] shrink-0 overflow-hidden rounded-2xl bg-sand-100">
      <img src={url} alt={alt} className="h-full w-full object-cover"/>
      {urls.length > 1 && <>
        <button type="button" onClick={() => setI(x => (x - 1 + urls.length) % urls.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center"><ChevronLeftIcon size={16}/></button>
        <button type="button" onClick={() => setI(x => (x + 1) % urls.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center"><ChevronRightIcon size={16}/></button>
        <div className="absolute bottom-2 right-2 rounded-full bg-black/50 text-white text-[10px] px-2 py-0.5">{i + 1}/{urls.length}</div>
      </>}
    </div>
  );
}

/* ─── Rate math ───────────────────────────────────────────────────────────── */

function cheapestNightly(hotel, stayNights) {
  let best = null;
  for (const room of hotel?.roomTypes || []) for (const rate of room.rates || []) {
    const total = Number(rate?.retailRate?.total?.[0]?.amount) || 0;
    if (!total) continue;
    const commission = Array.isArray(rate?.commission) ? Number(rate.commission[0]?.amount) || 0 : Number(rate?.commission?.amount) || 0;
    let inc = 0, exc = 0;
    for (const fee of rate?.retailRate?.taxesAndFees || []) { if (fee?.included) inc += Number(fee.amount) || 0; else exc += Number(fee.amount) || 0; }
    if (inc > 0.8 * total) inc = 0;
    const nightly = Math.max(0, Math.round((total - commission - inc) / (stayNights || 1)));
    const totalPrice = Math.max(0, Math.round(total + exc));
    if (!best || nightly < best.nightly) best = { nightly, total: totalPrice };
  }
  return best;
}

/* ─── Booking state (shared between sidebar + rooms list) ────────────────── */

const NEARBY_CANDIDATE_LIMIT = 20;
const NEARBY_SHOW = 3;
const haversineKm = (a, b, x, y) => {
  const R = 6371, toRad = d => d * Math.PI / 180;
  const dLat = toRad(x - a), dLng = toRad(y - b);
  const s = Math.sin(dLat/2)**2 + Math.cos(toRad(a)) * Math.cos(toRad(x)) * Math.sin(dLng/2)**2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

function useBookingState({ propertyId, rooms, propertyLat, propertyLng }) {
  const today = useMemo(() => new Date(), []);
  const initial = useMemo(() => new URLSearchParams(location.search), []);
  const [checkin, setCheckin] = useState(() => initial.get("checkin") || iso(addDays(today, 14)));
  const [checkout, setCheckout] = useState(() => initial.get("checkout") || iso(addDays(today, 17)));
  const [adults, setAdults] = useState(() => Number(initial.get("adults")) || 2);
  const [children, setChildren] = useState(() => (initial.get("children") || "").split(",").filter(Boolean).map(Number).filter(n => n >= 0 && n <= 17));
  const [openPanel, setOpenPanel] = useState("");     // "dates" | "guests" | ""
  const [phase, setPhase] = useState("start");
  const [hover, setHover] = useState("");
  const [calMonth, setCalMonth] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [rates, setRates] = useState(null);
  const [nearby, setNearby] = useState(null);       // null = not searched, [] = none, [...] = 1-3 alternatives
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cancelChoice, setCancelChoice] = useState({});
  const [extrasChoice, setExtrasChoice] = useState({});
  const boxRef = useRef(null);

  useEffect(() => {
    const onDown = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpenPanel(""); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const roomById = useMemo(() => { const m = {}; for (const r of rooms || []) m[String(r.id)] = r; return m; }, [rooms]);
  const stayNights = nights(checkin, checkout);

  const fetchNearby = useCallback(async () => {
    if (!propertyLat || !propertyLng || !checkin || !checkout || checkin >= checkout) { setNearby([]); return; }
    setNearbyLoading(true);
    try {
      const nights = Math.max(1, Math.round((parseISO(checkout) - parseISO(checkin)) / 86400000));
      const searchRadius = async (radius) => {
        const r = await fetch("/api/rates", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            latitude: propertyLat, longitude: propertyLng, radius,
            checkin, checkout,
            occupancies: [{ adults, ...(children.length ? { children } : {}) }],
            currency: "USD", guestNationality: "US",
            limit: 20, maxRatesPerHotel: 1, includeHotelData: true
          })
        });
        return r.ok ? r.json() : null;
      };
      const all = await fetch("/data/properties.json").then(r => r.ok ? r.json() : []).catch(() => []);
      const byId = new Map((all || []).map(p => [p.id, p]));
      const rowsFrom = (ratesRes) => (ratesRes?.data || [])
        .filter(h => h.hotelId !== propertyId && h.roomTypes?.length)
        .map(h => {
          const prop = byId.get(h.hotelId);
          if (!prop) return null;
          const cheapest = cheapestNightly(h, nights);
          if (!cheapest) return null;
          return { property: prop, price: cheapest, kind: "rate" };
        })
        .filter(Boolean)
        .sort((a, b) => (b.property.rating || 0) - (a.property.rating || 0) || a.price.nightly - b.price.nightly)
        .slice(0, NEARBY_SHOW);

      // Try 8 km → 25 km → 75 km. First one that yields matches wins.
      let rows = [];
      for (const radius of [8000, 25000, 75000]) {
        const res = await searchRadius(radius);
        rows = rowsFrom(res);
        if (rows.length) break;
      }

      // Final fallback: no live availability anywhere close — show top-rated
      // properties on the same island as an "explore the island" nudge.
      if (!rows.length) {
        const self = byId.get(propertyId);
        const island = self?.island;
        if (island) {
          rows = (all || [])
            .filter(p => p.island === island && p.id !== propertyId)
            .sort((a, b) => (b.rating || 0) - (a.rating || 0) || (b.reviewCount || 0) - (a.reviewCount || 0))
            .slice(0, NEARBY_SHOW)
            .map(prop => ({ property: prop, price: null, kind: "island" }));
        }
      }
      setNearby(rows);
    } catch {
      setNearby([]);
    } finally {
      setNearbyLoading(false);
    }
  }, [propertyLat, propertyLng, propertyId, checkin, checkout, adults, children]);

  const fetchRates = useCallback(async () => {
    if (!checkin || !checkout || checkin >= checkout) return;
    setLoading(true); setError(null); setRates(null); setCancelChoice({}); setExtrasChoice({});
    setNearby(null);
    try {
      const response = await fetch("/api/rates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hotelIds: [propertyId], checkin, checkout, occupancies: [{ adults, ...(children.length ? { children } : {}) }], currency: "USD", guestNationality: "US", roomMapping: true, includeHotelData: true })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      const roomTypes = [...(payload?.data?.[0]?.roomTypes || [])];

      console.log("[PropertyPage] /api/rates returned", { hotelId: propertyId, roomTypesFromApi: roomTypes.length, totalRatesFromApi: roomTypes.reduce((n, rt) => n + (rt.rates?.length || 0), 0) });

      // Carry-over: the exact rate the user clicked in search, stashed in
      // localStorage. It is AUTHORITATIVE — the property page re-asks LiteAPI
      // to discover the OTHER rooms, never to replace this one.
      //
      // It is always merged, never deduped away here. The old code tried to
      // dedup on `offerId|rateId`, but offerIds embed a per-search session
      // token so a carried offer can never equal one from this call — the
      // check could only ever pass. Same-room collisions are resolved at the
      // grouping step below, where the carried rate wins.
      const carried = readOffer(propertyId, checkin, checkout, adults, children);
      const carriedRT = offerAsRoomType(carried);
      if (carriedRT) {
        carriedRT.__carried = true;
        console.log("[PropertyPage] carry-over PINNED", {
          mappedRoomId: carriedRT.rates[0]?.mappedRoomId,
          name: carriedRT.name,
          total: carriedRT.rates[0]?.retailRate?.total?.[0]?.amount
        });
        roomTypes.unshift(carriedRT);
      } else {
        console.log("[PropertyPage] no carry-over stashed for these dates/guests");
      }

      const grouped = new Map();
      for (const room of roomTypes) for (const rate of room.rates || []) {
        const mappedId = rate.mappedRoomId ? String(rate.mappedRoomId) : null;
        const rateName = rate.name || room.name || "";
        const key = mappedId || rateName || room.offerId || "room";
        const details = roomById[mappedId];
        const priced = computeRate(rate, stayNights);
        const refundTag = rate.cancellationPolicies?.refundableTag || rate.refundableTag || "";
        const refundStatus = (refundTag === "RFN" || refundTag === "FREECANCELLATION") ? "refundable" : refundTag === "NRFN" ? "non-refundable" : "unknown";
        const cancelDate = (rate.cancellationPolicies?.cancelPolicyInfos?.[0]?.cancelTime || "").slice(0, 10) || null;
        const boardType = rate.boardType || "RO";
        const boardName = rate.boardName || BOARD_LABEL[boardType] || "No extras";
        const variant = {
          rateId: rate.rateId || "", offerId: room.offerId || rate.rateId || "", mappedRoomId: mappedId,
          name: details?.roomName || rateName || "Room",
          description: details?.description || "",
          refundStatus, refundTag, cancelDate, boardType, boardName,
          // Raw pieces the structured-data breakdown needs (itemised taxes
          // and fees, full cancel deadline, currency) — see HotelMicrodata.jsx.
          cancelTime: rate.cancellationPolicies?.cancelPolicyInfos?.[0]?.cancelTime || null,
          taxesAndFees: rate.retailRate?.taxesAndFees || [],
          currency: rate.retailRate?.total?.[0]?.currency || rate.retailRate?.total?.currency || "USD",
          ...priced,
          maxOccupancy: details?.maxOccupancy || null,
          roomSize: details?.roomSizeSquare || null,
          roomSizeUnit: details?.roomSizeUnit || "m²",
          bedTypes: details?.bedTypes || [],
          roomAmenities: details?.roomAmenities || [],
          isCarried: !!room.__carried
        };
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(variant);
      }
      const list = [];
      for (const [key, variants] of grouped) {
        // Same mapped room arriving twice (once carried, once from this call)
        // resolves to the carried one — that's the rate the guest actually
        // clicked, and it must not be swapped for a different price.
        const carriedVariant = variants.find(v => v.isCarried) || null;
        if (carriedVariant) {
          for (let i = variants.length - 1; i >= 0; i--) {
            const v = variants[i];
            if (v === carriedVariant) continue;
            // Drop API variants offering the same deal (same room, same board,
            // same refund terms) as the carried one; keep genuinely different
            // board / cancellation choices so the guest can still upgrade.
            if (v.refundStatus === carriedVariant.refundStatus && v.boardType === carriedVariant.boardType) {
              variants.splice(i, 1);
            }
          }
        }
        variants.sort((a, b) => a.totalPrice - b.totalPrice);
        // The card's headline is the carried rate when we have one, NOT the
        // cheapest — otherwise the price the guest clicked in search silently
        // becomes a different price on arrival.
        const headline = carriedVariant || variants[0];
        const cancelBy = {}, extrasBy = {}, lookup = {};
        for (const v of variants) {
          const cs = v.refundStatus, bt = v.boardType;
          if (!cancelBy[cs] || v.totalPrice < cancelBy[cs].minPrice) cancelBy[cs] = { status: cs, cancelDate: v.cancelDate, minPrice: v.totalPrice };
          if (!extrasBy[bt] || v.totalPrice < extrasBy[bt].minPrice) extrasBy[bt] = { type: bt, label: BOARD_LABEL[bt] || v.boardName, minPrice: v.totalPrice };
          const k = `${cs}-${bt}`;
          // Carried always wins its combo; otherwise cheapest wins.
          if (v.isCarried) lookup[k] = v;
          else if (!lookup[k] || (!lookup[k].isCarried && v.totalPrice < lookup[k].totalPrice)) lookup[k] = v;
        }
        list.push({
          key, ...headline, variants, isCarried: !!carriedVariant,
          // The cancel/board combo the guest arrived on. variantFor() defaults
          // to this instead of the cheapest combo, so the card opens showing
          // the rate they clicked.
          carriedRefund: carriedVariant?.refundStatus || null,
          carriedBoard: carriedVariant?.boardType || null,
          cancellationOptions: Object.values(cancelBy).sort((a,b)=>a.minPrice-b.minPrice),
          extrasOptions: Object.values(extrasBy).sort((a,b)=>a.minPrice-b.minPrice),
          variantLookup: lookup
        });
      }
      // The rate from search comes first; everything else loads in beneath it,
      // cheapest first.
      list.sort((a, b) => (b.isCarried === true) - (a.isCarried === true) || a.totalPrice - b.totalPrice);
      console.log("[PropertyPage] final grouped list", list.map(x => ({
        key: x.key,
        name: x.name,
        mappedRoomId: x.mappedRoomId,
        offerId: x.offerId,
        cheapestNightly: x.nightly,
        cheapestTotal: x.totalPrice,
        allVariants: x.variants.map(v => ({ rateId: v.rateId, offerId: v.offerId?.slice(0, 20), nightly: v.nightly, total: v.totalPrice, board: v.boardType, refund: v.refundStatus }))
      })));
      setRates(list);
      if (list.length === 0) fetchNearby();
    } catch (err) {
      console.warn("[PropertyPage] /api/rates threw", err);
      // Fetch failed — try to salvage with the carried offer so the user still
      // sees the rate they clicked from search. Only fall back to the error
      // state when there's nothing to show at all.
      const carried = readOffer(propertyId, checkin, checkout, adults, children);
      const carriedRT = offerAsRoomType(carried);
      if (carriedRT) {
        const rate = carriedRT.rates[0];
        const mappedId = rate.mappedRoomId ? String(rate.mappedRoomId) : null;
        const rateName = rate.name || carriedRT.name || "";
        const details = roomById[mappedId];
        const priced = computeRate(rate, stayNights);
        const refundTag = rate.cancellationPolicies?.refundableTag || rate.refundableTag || "";
        const refundStatus = (refundTag === "RFN" || refundTag === "FREECANCELLATION") ? "refundable" : refundTag === "NRFN" ? "non-refundable" : "unknown";
        const cancelDate = (rate.cancellationPolicies?.cancelPolicyInfos?.[0]?.cancelTime || "").slice(0, 10) || null;
        const boardType = rate.boardType || "RO";
        const boardName = rate.boardName || BOARD_LABEL[boardType] || "No extras";
        const variant = {
          rateId: rate.rateId || "", offerId: carriedRT.offerId || rate.rateId || "", mappedRoomId: mappedId,
          name: details?.roomName || rateName || "Room",
          description: details?.description || "",
          refundStatus, refundTag, cancelDate, boardType, boardName,
          // Raw pieces the structured-data breakdown needs (itemised taxes
          // and fees, full cancel deadline, currency) — see HotelMicrodata.jsx.
          cancelTime: rate.cancellationPolicies?.cancelPolicyInfos?.[0]?.cancelTime || null,
          taxesAndFees: rate.retailRate?.taxesAndFees || [],
          currency: rate.retailRate?.total?.[0]?.currency || rate.retailRate?.total?.currency || "USD",
          ...priced,
          maxOccupancy: details?.maxOccupancy || null,
          roomSize: details?.roomSizeSquare || null,
          roomSizeUnit: details?.roomSizeUnit || "m²",
          bedTypes: details?.bedTypes || [],
          roomAmenities: details?.roomAmenities || []
        };
        const key = mappedId || rateName || variant.offerId || "room";
        const cancelBy = { [refundStatus]: { status: refundStatus, cancelDate, minPrice: variant.totalPrice } };
        const extrasBy = { [boardType]: { type: boardType, label: BOARD_LABEL[boardType] || boardName, minPrice: variant.totalPrice } };
        setRates([{ key, ...variant, variants: [variant], cancellationOptions: Object.values(cancelBy), extrasOptions: Object.values(extrasBy), variantLookup: { [`${refundStatus}-${boardType}`]: variant } }]);
      } else {
        setError("Could not load rates. Please try again.");
      }
    } finally { setLoading(false); }
  }, [checkin, checkout, adults, children, propertyId, roomById, stayNights]);

  // Price the default stay even when the URL carries no dates. Without this a
  // bare /properties/<id> URL — which is the canonical, the one in the sitemap
  // and the one Google actually crawls — rendered "pick your dates" and had no
  // price at all, visibly or in the structured data. Hotel Center's own
  // landing pages always carry dates, so only organic traffic was affected,
  // but that is exactly what the Rich Results Test loads.
  const autoPriced = useRef(false);
  useEffect(() => {
    if (autoPriced.current) return;
    if (!checkin || !checkout || checkin >= checkout) return;
    autoPriced.current = true;
    fetchRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkin, checkout]);

  const search = () => {
    const q = new URLSearchParams(location.search);
    q.set("checkin", checkin); q.set("checkout", checkout); q.set("adults", String(adults));
    if (children.length) q.set("children", children.join(",")); else q.delete("children");
    history.replaceState(null, "", `${location.pathname}?${q}`);
    fetchRates();
  };

  const pickDate = v => {
    if (phase === "start" || !checkin || v <= checkin) { setCheckin(v); setCheckout(""); setPhase("end"); setHover(""); }
    else { setCheckout(v); setPhase("start"); setHover(""); }
  };

  const variantFor = g => {
    // Until the guest picks something, a carried group shows the exact combo
    // they clicked in search — not the cheapest one on the page.
    const cs = cancelChoice[g.key] || g.carriedRefund || g.cancellationOptions[0]?.status || "unknown";
    const bt = extrasChoice[g.key] || g.carriedBoard || g.extrasOptions[0]?.type || "RO";
    return g.variantLookup[`${cs}-${bt}`] || g.variants[0];
  };

  return {
    checkin, setCheckin, checkout, setCheckout, adults, setAdults, children, setChildren,
    openPanel, setOpenPanel, phase, setPhase, hover, setHover, calMonth, setCalMonth,
    rates, loading, error, search, pickDate, boxRef, today, stayNights,
    cancelChoice, setCancelChoice, extrasChoice, setExtrasChoice, variantFor,
    nearby, nearbyLoading
  };
}

function BookingBar({ st }) {
  const { checkin, checkout, adults, children, openPanel, setOpenPanel, phase, setPhase, hover, setHover, calMonth, setCalMonth, today, loading, search, pickDate, boxRef, setCheckin, setCheckout, setAdults, setChildren } = st;
  return (
    <div ref={boxRef} className="relative">
      <div className="rounded-2xl bg-white ring-1 ring-ink/10 shadow-card flex flex-col md:flex-row md:items-stretch overflow-hidden">
        <button type="button"
          onClick={() => { setOpenPanel(openPanel === "dates" ? "" : "dates"); setCalMonth(new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1)); setPhase("start"); setHover(""); }}
          className={`flex-1 text-left px-5 py-3 border-b md:border-b-0 md:border-r border-ink/10 transition ${openPanel === "dates" ? "bg-sand-50" : "hover:bg-sand-50/60"}`}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-in</div>
          <div className="text-sm font-medium text-ink">{fmtShort(checkin) || "Add date"}</div>
        </button>
        <button type="button"
          onClick={() => { setOpenPanel(openPanel === "dates" ? "" : "dates"); setCalMonth(new Date((parseISO(checkin) || today).getFullYear(), (parseISO(checkin) || today).getMonth(), 1)); setPhase(checkin && !checkout ? "end" : "start"); setHover(""); }}
          className={`flex-1 text-left px-5 py-3 border-b md:border-b-0 md:border-r border-ink/10 transition ${openPanel === "dates" ? "bg-sand-50" : "hover:bg-sand-50/60"}`}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-out</div>
          <div className="text-sm font-medium text-ink">{fmtShort(checkout) || "Add date"}</div>
        </button>
        <button type="button" onClick={() => setOpenPanel(openPanel === "guests" ? "" : "guests")}
          className={`flex-1 text-left px-5 py-3 border-b md:border-b-0 md:border-r border-ink/10 transition ${openPanel === "guests" ? "bg-sand-50" : "hover:bg-sand-50/60"}`}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Travelers</div>
          <div className="text-sm font-medium text-ink">{adults} adult{adults !== 1 ? "s" : ""}{children.length ? ` · ${children.length} child${children.length !== 1 ? "ren" : ""}` : ""}</div>
        </button>
        <button className="btn-primary shrink-0 rounded-none md:rounded-none px-6 py-3 md:min-w-[140px] justify-center" onClick={search} disabled={loading || !checkin || !checkout || checkin >= checkout}>
          {loading ? "Searching…" : "Search rates"}
        </button>
      </div>

      {openPanel === "dates" && (
        <div className="absolute left-0 right-0 md:left-auto md:right-0 top-full mt-2 z-40 w-full md:w-[720px] rounded-3xl bg-white shadow-cardHover ring-1 ring-ink/5 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-in</div>
                <div className="font-semibold text-ink">{checkin ? fmtLong(checkin) : "Select date"}</div>
              </div>
              <div className="text-ink-muted">→</div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Check-out</div>
                <div className="font-semibold text-ink">{checkout ? fmtLong(checkout) : "Select date"}</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()-1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Previous month"><ChevronLeftIcon size={16}/></button>
              <button type="button" onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1))} className="h-9 w-9 rounded-full border border-ink/10 flex items-center justify-center hover:bg-sand-50" aria-label="Next month"><ChevronRightIcon size={16}/></button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CalendarMonth month={calMonth} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={pickDate}/>
            <CalendarMonth month={new Date(calMonth.getFullYear(), calMonth.getMonth()+1, 1)} today={today} start={checkin} end={checkout} phase={phase} hover={hover} onHover={setHover} onPick={pickDate}/>
          </div>
          <div className="mt-5 pt-4 border-t border-ink/5 flex items-center justify-between">
            <button type="button" onClick={() => { setCheckin(""); setCheckout(""); setPhase("start"); setHover(""); }} className="text-sm text-ink-muted hover:text-ink underline">Clear dates</button>
            <button type="button" onClick={() => setOpenPanel("")} className="btn-primary py-2 px-6 text-sm" disabled={!checkin || !checkout}>Apply</button>
          </div>
        </div>
      )}

      {openPanel === "guests" && (
        <div className="absolute right-0 top-full mt-2 z-40 w-full sm:w-[360px] rounded-3xl bg-white shadow-cardHover ring-1 ring-ink/5 p-5">
          <StepperRow label="Adults" hint="Ages 13+" value={adults} min={1} max={12} onChange={setAdults}/>
          <div className="h-px bg-ink/5 my-3"/>
          <StepperRow label="Children" hint="Ages 0–17" value={children.length} min={0} max={8}
            onChange={n => setChildren(c => n > c.length ? [...c, ...Array(n - c.length).fill(5)] : c.slice(0, n))}/>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => setOpenPanel("")} className="btn-primary py-2 px-6 text-sm">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function PropertyMap({ property }) {
  const lat = property.location?.latitude ?? property.lat;
  const lng = property.location?.longitude ?? property.lng;
  if (!lat || !lng) return null;
  const q = `${lat},${lng}`;
  const embed = `https://www.google.com/maps?q=${q}&hl=en&z=15&output=embed`;
  const link = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  return (
    <div className="card overflow-hidden shadow-cardHover ring-1 ring-ink/5 lg:sticky lg:top-24">
      <div className="aspect-square bg-sand-100">
        <iframe
          title={`Map of ${property.name}`}
          src={embed}
          className="w-full h-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      <div className="p-5">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Where you'll be</div>
        <div className="mt-1 font-display text-lg text-ink">{property.area || property.city || property.island}</div>
        {property.address && <p className="mt-1 text-sm text-ink-muted flex items-start gap-1.5"><PinIcon size={14} className="mt-0.5 shrink-0 text-ocean-600"/>{property.address}</p>}
        <a href={link} target="_blank" rel="noopener noreferrer" className="mt-4 btn-secondary text-sm inline-flex w-full justify-center">Open in Google Maps</a>
      </div>
    </div>
  );
}

function MapLightbox({ property, onClose }) {
  const lat = property.location?.latitude ?? property.lat;
  const lng = property.location?.longitude ?? property.lng;
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose]);
  const q = `${lat},${lng}`;
  const embed = `https://www.google.com/maps?q=${q}&hl=en&z=15&output=embed`;
  const link = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  return (
    <div className="fixed inset-0 z-[100] bg-white flex flex-col">
      <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-ink/10 bg-white">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Where you'll be</div>
          <div className="font-display text-base text-ink truncate">{property.area || property.city || property.island}</div>
        </div>
        <button onClick={onClose} className="h-9 w-9 rounded-full border border-ink/15 hover:bg-sand-100 flex items-center justify-center shrink-0 ml-3" aria-label="Close"><CloseIcon size={16}/></button>
      </div>
      <div className="flex-1 bg-sand-100">
        <iframe title={`Map of ${property.name}`} src={embed} className="w-full h-full border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>
      </div>
      {property.address && (
        <div className="border-t border-ink/10 p-4 bg-white">
          <p className="text-sm text-ink-muted flex items-start gap-1.5"><PinIcon size={14} className="mt-0.5 shrink-0 text-ocean-600"/>{property.address}</p>
          <a href={link} target="_blank" rel="noopener noreferrer" className="mt-3 btn-secondary text-sm inline-flex w-full justify-center">Open in Google Maps</a>
        </div>
      )}
    </div>
  );
}

function StepperRow({ label, hint, value, min, max, onChange }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-ink">{label}</div>
        <div className="text-xs text-ink-muted">{hint}</div>
      </div>
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => onChange(Math.max(min, value-1))} disabled={value <= min} className="h-9 w-9 rounded-full border border-ink/15 flex items-center justify-center text-ink hover:border-ocean-400 disabled:opacity-30">−</button>
        <span className="w-8 text-center text-sm font-medium text-ink">{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value+1))} disabled={value >= max} className="h-9 w-9 rounded-full border border-ink/15 flex items-center justify-center text-ink hover:border-ocean-400 disabled:opacity-30">+</button>
      </div>
    </div>
  );
}

function RoomDetailsTrigger({ onOpen, hasContent }) {
  if (!hasContent) return null;
  return (
    <button type="button" onClick={onOpen} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-ocean-700 hover:text-ocean-800">
      More details
      <ChevronRightIcon size={12}/>
    </button>
  );
}

function RoomDetailsModal({ open, onClose, group, propertyId, propertyName, reserveHref, nightlyEach, totalCost, bedText }) {
  const [imgs, setImgs] = useState([]);
  const [fallbackDesc, setFallbackDesc] = useState("");
  const [probed, setProbed] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    if (!open) { setProbed(true); return; }
    let cancelled = false;
    setImgs([]);
    setProbed(false);
    setHeroIdx(0);
    setFallbackDesc("");

    // Always fetch hotel-data (module-level cache = one request per property
    // per page load). R2 photos are treated as the fast, thumbnail-optimized
    // source; API photos fill in when R2 is missing / partial. Same for the
    // description — local wins, remote fills the gap.
    // The room key comes from the details (same resolution as the card's
    // RoomImage: mappedRoomId, else the property's single catalog room).
    fetchHotelDetails(propertyId).then(async details => {
      if (cancelled) return;
      const key = resolveRoomKey(details.roomsById, group?.mappedRoomId);
      const probes = key
        ? Array.from({ length: 16 }, (_, i) => new Promise(resolve => {
            const url = `${IMAGE_ROOT}/rooms/${propertyId}/${key}/${i}.webp`;
            const img = new Image();
            img.onload = () => resolve(url);
            img.onerror = () => resolve(null);
            img.src = url;
          }))
        : [Promise.resolve(null)];
      const results = await Promise.all(probes);
      if (cancelled) return;
      const r2Urls = results.filter(Boolean);
      const remoteUrls = key ? (details.roomsById.get(key) || []) : [];
      const seen = new Set(r2Urls);
      const merged = [...r2Urls, ...remoteUrls.filter(u => !seen.has(u))];
      const remoteDesc = key ? (details.roomDescriptionsById.get(key) || "") : "";
      console.log("[RoomDetailsModal] open", { propertyId, mappedRoomId: group?.mappedRoomId, resolvedKey: key, r2: r2Urls.length, remote: remoteUrls.length, merged: merged.length, hasLocalDesc: !!group?.description, hasRemoteDesc: !!remoteDesc, availableRoomKeys: [...details.roomsById.keys()].slice(0, 5) });
      setImgs(merged);
      if (!group?.description && key) setFallbackDesc(remoteDesc);
      setProbed(true);
    });
    return () => { cancelled = true; };
  }, [open, propertyId, group?.mappedRoomId, group?.description]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = e => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  if (!open || !group) return null;

  const amens = Array.isArray(group.roomAmenities) ? group.roomAmenities.filter(a => a?.name) : [];

  return (
    <div className="fixed inset-0 z-[90] bg-ink/60 backdrop-blur-sm flex items-start md:items-center justify-center p-2 md:p-6" onClick={onClose}>
      <div className="relative bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] shadow-cardHover flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/5">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted">Room details</div>
            <h3 className="font-display text-xl md:text-2xl leading-tight text-ink truncate">{group.name}</h3>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 h-10 w-10 rounded-full bg-ink/5 hover:bg-ink/10 flex items-center justify-center" aria-label="Close">
            <CloseIcon size={18}/>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!probed ? (
            <div className="bg-sand-100">
              <div className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden bg-sand-200 animate-pulse"/>
              <div className="flex gap-2 overflow-hidden p-3">
                {[0,1,2,3,4,5].map(i => <div key={i} className="h-16 w-24 shrink-0 rounded-lg bg-sand-200 animate-pulse"/>)}
              </div>
            </div>
          ) : imgs.length > 0 ? (
            <div className="bg-sand-100">
              <div className="relative aspect-[16/10] md:aspect-[21/9] overflow-hidden">
                <img src={imgs[heroIdx]} alt={group.name} className="h-full w-full object-cover"/>
                {imgs.length > 1 && <>
                  <button type="button" onClick={() => setHeroIdx(i => (i - 1 + imgs.length) % imgs.length)} className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center" aria-label="Previous"><ChevronLeftIcon size={18}/></button>
                  <button type="button" onClick={() => setHeroIdx(i => (i + 1) % imgs.length)} className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center" aria-label="Next"><ChevronRightIcon size={18}/></button>
                  <div className="absolute bottom-3 right-3 rounded-full bg-black/55 text-white text-xs px-2.5 py-1">{heroIdx + 1} / {imgs.length}</div>
                </>}
              </div>
              {imgs.length > 1 && (
                <div className="flex gap-2 overflow-x-auto p-3">
                  {imgs.map((url, i) => (
                    <button key={url} onClick={() => setHeroIdx(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg ring-2 transition ${i === heroIdx ? "ring-ocean-500" : "ring-transparent opacity-60 hover:opacity-100"}`}>
                      <img src={url} alt="" className="h-full w-full object-cover"/>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-sand-100 aspect-[16/9] grid place-items-center text-ink-muted text-sm">No room photos available</div>
          )}

          <div className="p-6 space-y-6">
            {(group.maxOccupancy || bedText || group.roomSize) && (
              <div className="flex flex-wrap gap-2">
                {group.roomSize && <span className="inline-flex items-center gap-1.5 rounded-pill bg-sand-100 text-ink-soft px-3 py-1.5 text-xs">{group.roomSize} {group.roomSizeUnit}</span>}
                {group.maxOccupancy && <span className="inline-flex items-center gap-1.5 rounded-pill bg-sand-100 text-ink-soft px-3 py-1.5 text-xs">Sleeps {group.maxOccupancy}</span>}
                {bedText && <span className="inline-flex items-center gap-1.5 rounded-pill bg-sand-100 text-ink-soft px-3 py-1.5 text-xs">{bedText}</span>}
              </div>
            )}

            {(group.description || fallbackDesc) && (
              <div>
                <h4 className="font-display text-lg text-ink mb-2">About this room</h4>
                <div className="prose-body text-sm text-ink-soft leading-relaxed [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_strong]:text-ink [&_b]:text-ink" dangerouslySetInnerHTML={{ __html: group.description || fallbackDesc }}/>
              </div>
            )}

            {amens.length > 0 && (
              <div>
                <h4 className="font-display text-lg text-ink mb-3">In this room</h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-ink-soft">
                  {amens.map(a => (
                    <li key={a.amenitiesId || a.name} className="flex items-start gap-2">
                      <CheckBadgeIcon size={16} className="text-ocean-600 mt-0.5 shrink-0"/>
                      <span>{a.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-ink/5 bg-white px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-2xl font-semibold text-ink leading-none">${nightlyEach.toLocaleString()}<span className="text-xs font-normal text-ink-muted"> / night</span></div>
            <div className="text-[11px] text-ink-muted mt-1">${totalCost.toLocaleString()} total · fees included</div>
          </div>
          <a href={reserveHref} className="btn-sunset shrink-0">Reserve</a>
        </div>
      </div>
    </div>
  );
}

function NearbyCard({ property, price, checkin, checkout, adults, children }) {
  const slug = (property.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const q = new URLSearchParams({ checkin, checkout, adults: String(adults) });
  if (children.length) q.set("children", children.join(","));
  const href = `/properties/${property.id}-${slug}?${q}`;
  return (
    <a href={href} className="card overflow-hidden group flex flex-col">
      <div className="relative aspect-[4/3] overflow-hidden bg-sand-100">
        <img src={`https://images.aloharates.com/properties/${property.id}/0.webp`} alt={property.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" onError={e => { e.currentTarget.style.display = "none"; }}/>
      </div>
      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <div className="text-[11px] text-ink-muted flex items-center gap-1"><PinIcon size={11}/>{[property.area || property.city, property.island].filter(Boolean).join(", ")}</div>
        <h4 className="font-display text-base leading-snug text-ink line-clamp-2 min-h-[2.6em]">{property.name}</h4>
        {property.rating > 0 && (
          <div className="flex items-center gap-1 text-xs">
            <StarIcon size={12} className="text-sunset-500 fill-current"/>
            <span className="font-medium">{Number(property.rating).toFixed(1)}</span>
            <span className="text-ink-muted">· {(property.reviewCount || 0).toLocaleString()}</span>
          </div>
        )}
        <div className="mt-auto pt-2 border-t border-ink/5 flex items-baseline justify-between">
          <div>
            {price ? (
              <>
                <div className="text-lg font-semibold text-ink">${price.nightly.toLocaleString()}<span className="text-[11px] font-normal text-ink-muted"> / night</span></div>
                <div className="text-[10px] text-ink-muted">${price.total.toLocaleString()} total</div>
              </>
            ) : (
              <div className="text-xs text-ink-muted">Check availability</div>
            )}
          </div>
          <span className="text-xs font-medium text-ocean-700">View →</span>
        </div>
      </div>
    </a>
  );
}

function RoomsBlock({ loading, error, rates, propertyId, propertyName, propertyArea, propertyIsland, checkin, checkout, adults, children, stayNights, cancelChoice, setCancelChoice, extrasChoice, setExtrasChoice, variantFor, bookingState, nearby, nearbyLoading, times }) {
  // "Compare other <area> stays" → the same /results/ URL the homepage search
  // builds when a town/area suggestion is picked, carrying the current stay.
  const areaLabel = propertyArea || islandDisplayFor({ island: propertyIsland });
  const compareHref = (() => {
    if (!areaLabel) return "";
    const today = new Date();
    const q = new URLSearchParams({
      checkin: checkin || iso(addDays(today, 14)),
      checkout: checkout || iso(addDays(today, 17)),
      adults: String(adults)
    });
    if (propertyArea) q.set("q", propertyArea);
    const slug = propertyIsland ? islandSlugFor({ island: propertyIsland }) : "";
    if (slug) q.set("island", slug);
    if (children.length) q.set("children", children.join(","));
    return `/results/?${q}`;
  })();
  // Property check-in / check-out clock times → the offer's LodgingReservation datetimes.
  const checkinClock = times?.checkin_start || times?.checkinStart || "";
  const checkoutClock = times?.checkout || times?.checkoutEnd || "";
  const [modalKey, setModalKey] = useState(null);
  // Per-room result of the photo/description lookup, reported by RoomImage.
  // Until it arrives the "More details" trigger is shown (optimistic); once we
  // know a room has no photos, no description and no amenity list, it hides.
  const [roomMedia, setRoomMedia] = useState({});
  const detailsHaveContent = (group) => {
    const media = roomMedia[group.key];
    if (!media) return true;
    const amens = Array.isArray(group.roomAmenities) ? group.roomAmenities.filter(a => a?.name) : [];
    return media.photos > 0 || !!group.description || media.remoteDescription || amens.length > 0;
  };

  const header = (
    <div className="mb-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="section-title">Rooms &amp; rates</h2>
          {/* Stay dates + occupancy carry Microdata for the page-level
              Hotel + LodgingReservation scope (Hotel Center's simplified tree). */}
          {checkin && checkout && (
            <p className="text-sm text-ink-muted mt-1">
              {stayNights} night{stayNights !== 1 ? "s" : ""} · <span itemProp="checkinTime" content={checkin}>{fmtShort(checkin)}</span> – <span itemProp="checkoutTime" content={checkout}>{fmtShort(checkout)}</span> · <span itemProp="numAdults">{adults}</span> adult{adults !== 1 ? "s" : ""}{children.length ? <> · <span itemProp="numChildren">{children.length}</span> child{children.length !== 1 ? "ren" : ""}</> : null}
            </p>
          )}
        </div>
        {compareHref && (
          <a href={compareHref} className="text-sm font-medium text-ocean-700 hover:text-ocean-800 inline-flex items-center gap-1">
            Compare other {areaLabel} stays for these dates <ArrowRightIcon size={14}/>
          </a>
        )}
      </div>
      {bookingState && <div className="mt-4"><BookingBar st={bookingState}/></div>}
    </div>
  );

  if (error) return <section>{header}<div className="card p-6 border-l-4 border-sunset-500 text-sm text-ink-soft">{error}</div></section>;
  if (loading) return (
    <section>
      {header}
      <div className="space-y-3" {...NAV.loading}>
        {[0,1,2].map(i => <div key={i} className="card p-4 flex gap-4"><div className="w-[220px] aspect-[4/3] bg-sand-200 rounded-2xl animate-pulse"/><div className="flex-1 space-y-2"><div className="h-4 w-2/3 bg-sand-200 rounded animate-pulse"/><div className="h-3 w-1/3 bg-sand-200 rounded animate-pulse"/><div className="h-3 w-1/2 bg-sand-200 rounded animate-pulse"/></div></div>)}
      </div>
    </section>
  );
  if (!rates) return (
    <section>
      {header}
      <div className="card p-8 text-center">
        <CalendarIcon size={36} className="mx-auto text-ocean-600"/>
        <div className="font-display text-xl text-ink mt-3">Pick your dates to see rooms</div>
        <p className="prose-body mt-2">Rates change day to day. Set your check-in and check-out to see live prices for every available room.</p>
      </div>
    </section>
  );
  if (rates.length === 0) return (
    <section>
      {header}
      <div className="card p-6 md:p-8">
        {/* Sold-out itineraries still need an offer (availability SoldOut, no price). */}
        <SoldOutOffer checkin={checkin} checkout={checkout}/>
        <div className="text-center">
          <div className="font-display text-xl md:text-2xl text-ink">Fully booked for those dates</div>
          <p className="prose-body mt-2">{propertyName} has no availability for {fmtShort(checkin)} – {fmtShort(checkout)}. Try shifting your dates a day or two{nearby && nearby.length ? (nearby[0].kind === "island" ? ", or explore more of the island" : ", or check out these places nearby") : ""}.</p>
        </div>
        {nearbyLoading && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0,1,2].map(i => <div key={i} className="card overflow-hidden"><div className="aspect-[4/3] bg-sand-200 animate-pulse"/><div className="p-4 space-y-2"><div className="h-3 w-1/3 bg-sand-200 rounded animate-pulse"/><div className="h-4 w-3/4 bg-sand-200 rounded animate-pulse"/><div className="h-3 w-1/2 bg-sand-200 rounded animate-pulse"/></div></div>)}
          </div>
        )}
        {!nearbyLoading && nearby && nearby.length > 0 && (
          <div className="mt-8">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-3 text-center">{nearby[0].kind === "island" ? "More on this island" : "Available nearby · same dates"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nearby.map(n => <NearbyCard key={n.property.id} property={n.property} price={n.price} checkin={checkin} checkout={checkout} adults={adults} children={children}/>)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
  return (
    <section>
      {header}
      <div className="space-y-4">
        {rates.map(group => {
          const chosen = variantFor(group);
          const nightlyEach = Math.round((chosen.baseRate - chosen.commission - chosen.feesIncluded) / (stayNights || 1));
          const totalCost = Math.round(chosen.baseRate + chosen.feesExcluded);
          const cheapestTotal = group.variants[0].totalPrice;
          // Only ONE offer may carry price Microdata on the landing page: the
          // headline room (the rate carried from search, else the cheapest).
          // Its total is the "total · fees included" figure shown on screen.
          const primary = group === rates[0];
          const breakdown = primary ? priceBreakdown({ payToday: chosen.baseRate, feesIncluded: chosen.feesIncluded, taxesAndFees: chosen.taxesAndFees, total: totalCost, currency: chosen.currency }) : null;
          const currentCancel = cancelChoice[group.key] || group.cancellationOptions[0]?.status;
          const currentExtras = extrasChoice[group.key] || group.extrasOptions[0]?.type;
          const bedText = group.bedTypes.map(b => b.quantity && b.bedType ? `${b.quantity}× ${b.bedType}` : b.bedType || "").filter(Boolean).join(", ");
          const availExtras = group.extrasOptions.filter(o => group.variantLookup[`${currentCancel}-${o.type}`]);
          const hasExtras = availExtras.some(o => o.type !== "RO");
          const reserveHref = `/checkout/?hotelId=${propertyId}&offerId=${chosen.offerId}&checkin=${checkin}&checkout=${checkout}&adults=${adults}${children.length ? `&children=${children.join(",")}` : ""}&hotelName=${encodeURIComponent(propertyName || "")}&roomName=${encodeURIComponent(chosen.name || "")}&boardName=${encodeURIComponent(chosen.boardName || "")}&refundable=${chosen.refundStatus === "refundable" ? "1" : "0"}&cancelDate=${encodeURIComponent(chosen.cancelDate || "")}`;
          return (
            <div key={group.key} className="card p-4 md:p-5" {...(primary ? { itemProp: "makesOffer", itemScope: true, itemType: OFFER_TYPE } : {})}>
              {primary && (
                <span hidden>
                  <meta itemProp="name" content={chosen.name || "Room"}/>
                  <meta itemProp="identifier" content={chosen.rateId || chosen.offerId || ""}/>
                  <meta itemProp="checkinTime" content={stayDateTime(checkin, checkinClock)}/>
                  <meta itemProp="checkoutTime" content={stayDateTime(checkout, checkoutClock, "11:00:00")}/>
                  <meta itemProp="availability" content="https://schema.org/InStock"/>
                  <span itemProp="occupancy" itemScope itemType="https://schema.org/QuantitativeValue"><meta itemProp="value" content={String(adults + children.length)}/></span>
                  <ReturnPolicy refundable={chosen.refundStatus === "refundable"} cancelTime={cancelDateTime(chosen.cancelTime || chosen.cancelDate)}/>
                </span>
              )}
              <div className="flex flex-col md:flex-row gap-4 md:gap-5">
                <RoomImage propertyId={propertyId} mappedRoomId={group.mappedRoomId} alt={group.name}
                  onResolved={media => setRoomMedia(prev => ({ ...prev, [group.key]: media }))}/>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display text-xl leading-snug text-ink">{group.name}</h3>
                  {(group.maxOccupancy || bedText || group.roomSize) && (
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-muted">
                      {group.maxOccupancy && <span>Sleeps {group.maxOccupancy}</span>}
                      {bedText && <span>{bedText}</span>}
                      {group.roomSize && <span>{group.roomSize} {group.roomSizeUnit}</span>}
                    </div>
                  )}
                  <RoomDetailsTrigger onOpen={() => setModalKey(group.key)} hasContent={detailsHaveContent(group)}/>
                  {/* Cancellation options */}
                  {group.cancellationOptions.length > 1 ? (
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-1.5">Cancellation</div>
                      <div className="space-y-1">
                        {group.cancellationOptions.map(opt => {
                          const selected = currentCancel === opt.status;
                          const v = group.variants.filter(v => v.refundStatus === opt.status).sort((a,b)=>a.totalPrice-b.totalPrice)[0];
                          const delta = ((v?.totalPrice) || 0) - cheapestTotal;
                          return (
                            <label key={opt.status} className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer text-sm ${selected ? "border-ocean-500 bg-ocean-50" : "border-ink/10 hover:border-ink/25"}`}>
                              <input type="radio" name={`cancel-${group.key}`} checked={selected} onChange={() => setCancelChoice(x => ({ ...x, [group.key]: opt.status }))} className="accent-ocean-600"/>
                              <span className="flex-1">{opt.status === "refundable" ? <span className="text-ocean-700 font-medium">{opt.cancelDate ? `Free cancel before ${fmtShort(opt.cancelDate)}` : "Free cancellation"}</span> : <span>Non-refundable</span>}</span>
                              <span className="text-xs text-ink-muted">{delta === 0 ? "+$0" : delta > 0 ? `+$${Math.round(delta)}` : `-$${Math.abs(Math.round(delta))}`}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3">
                      {group.cancellationOptions[0]?.status === "refundable"
                        ? <span className="inline-flex items-center gap-1.5 rounded-full bg-ocean-50 text-ocean-700 px-3 py-1 text-xs font-medium"><CheckBadgeIcon size={12}/> {group.cancellationOptions[0].cancelDate ? `Free cancel before ${fmtShort(group.cancellationOptions[0].cancelDate)}` : "Free cancellation"}</span>
                        : <span className="inline-flex items-center gap-1.5 rounded-full bg-sand-100 text-ink-muted px-3 py-1 text-xs font-medium">Non-refundable</span>}
                    </div>
                  )}
                  {/* Extras */}
                  {hasExtras && availExtras.length > 1 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-muted mb-1.5">Extras</div>
                      <div className="flex flex-wrap gap-1.5">
                        {availExtras.map(opt => {
                          const selected = currentExtras === opt.type;
                          return (
                            <button key={opt.type} type="button" onClick={() => setExtrasChoice(x => ({ ...x, [group.key]: opt.type }))} className={`text-xs rounded-full px-3 py-1.5 border ${selected ? "border-ocean-500 bg-ocean-50 text-ocean-700" : "border-ink/10 text-ink-soft hover:border-ink/25"}`}>{opt.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {/* Price + reserve */}
                <div className="md:w-[180px] md:text-right flex md:flex-col justify-between items-end md:items-stretch gap-2 md:gap-3 md:border-l md:border-ink/5 md:pl-5">
                  <div>
                    <div className="text-2xl md:text-3xl font-semibold text-ink">${nightlyEach.toLocaleString()}<span className="text-xs font-normal text-ink-muted"> / night</span></div>
                    {primary ? (
                      <div className="text-[11px] text-ink-muted" itemProp="priceSpecification" itemScope itemType={COMPOUND_TYPE}>
                        <span itemProp="priceCurrency" content={breakdown.currency}>$</span><span itemProp="price" content={money2(totalCost)}>{totalCost.toLocaleString()}</span> total · taxes &amp; fees included
                        {breakdown.lines.map((line, i) => <PriceComponent key={i} line={line} currency={breakdown.currency} hotelName={propertyName}/>)}
                      </div>
                    ) : (
                      <div className="text-[11px] text-ink-muted">${totalCost.toLocaleString()} total · taxes &amp; fees included</div>
                    )}
                  </div>
                  {/* Hotel Center's crawler follows the headline room's Reserve
                      into checkout, where the final Microdata price lives. */}
                  <a href={reserveHref} className="btn-sunset text-sm px-4 py-2.5 md:w-full" {...(primary ? NAV.click(1) : {})}>Reserve</a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <RoomDetailsModal
        open={!!modalKey}
        onClose={() => setModalKey(null)}
        group={rates.find(g => g.key === modalKey)}
        propertyId={propertyId}
        propertyName={propertyName}
        reserveHref={(() => {
          const g = rates.find(g => g.key === modalKey);
          if (!g) return "#";
          const chosen = variantFor(g);
          return `/checkout/?hotelId=${propertyId}&offerId=${chosen.offerId}&checkin=${checkin}&checkout=${checkout}&adults=${adults}${children.length ? `&children=${children.join(",")}` : ""}&hotelName=${encodeURIComponent(propertyName || "")}&roomName=${encodeURIComponent(chosen.name || "")}&boardName=${encodeURIComponent(chosen.boardName || "")}&refundable=${chosen.refundStatus === "refundable" ? "1" : "0"}&cancelDate=${encodeURIComponent(chosen.cancelDate || "")}`;
        })()}
        nightlyEach={(() => {
          const g = rates.find(g => g.key === modalKey);
          if (!g) return 0;
          const chosen = variantFor(g);
          return Math.round((chosen.baseRate - chosen.commission - chosen.feesIncluded) / (stayNights || 1));
        })()}
        totalCost={(() => {
          const g = rates.find(g => g.key === modalKey);
          if (!g) return 0;
          const chosen = variantFor(g);
          return Math.round(chosen.baseRate + chosen.feesExcluded);
        })()}
        bedText={(() => {
          const g = rates.find(g => g.key === modalKey);
          if (!g) return "";
          return g.bedTypes.map(b => b.quantity && b.bedType ? `${b.quantity}× ${b.bedType}` : b.bedType || "").filter(Boolean).join(", ");
        })()}
      />
    </section>
  );
}

/* ─── Reviews ────────────────────────────────────────────────────────────── */

function Reviews({ hotelId, hotelName, rating, reviewCount, sentimentAnalysis }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("All");
  const [limit, setLimit] = useState(4);
  const [expanded, setExpanded] = useState(new Set());
  const sentinel = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!hotelId || started.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && !started.current) {
        started.current = true;
        observer.disconnect();
        setLoading(true);
        fetch(`/api/reviews?hotelId=${hotelId}&limit=5000`)
          .then(r => r.ok ? r.json() : { data: [] })
          .then(payload => setItems(((payload?.data || payload?.reviews || []).filter(r => r.pros || r.cons || r.headline))))
          .catch(() => {})
          .finally(() => setLoading(false));
      }
    }, { rootMargin: "200px" });
    if (sentinel.current) observer.observe(sentinel.current);
    return () => observer.disconnect();
  }, [hotelId]);

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== "All") {
      const keys = REVIEW_FILTER_KEYS[filter] || [];
      list = items.filter(r => keys.some(k => (r.type || "").toLowerCase().includes(k)));
    }
    return [...list].sort((a, b) => {
      const s = (parseFloat(b.averageScore) || 0) - (parseFloat(a.averageScore) || 0);
      return s !== 0 ? s : (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0);
    });
  }, [items, filter]);

  const sentiment = sentimentAnalysis || {};
  const pros = sentiment.pros || [];
  const cats = sentiment.categories || [];

  return (
    <section ref={sentinel}>
      <h2 className="section-title">Ratings &amp; reviews</h2>
      {rating > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 items-start">
          <div className="flex items-center gap-4">
            <div className="rounded-2xl bg-ocean-700 text-white text-2xl font-semibold px-4 py-2">{Number(rating).toFixed(1)}</div>
            <div>
              <div className="font-display text-xl text-ink">{ratingLabel(rating)}</div>
              {reviewCount > 0 && <div className="text-sm text-ink-muted">From {reviewCount.toLocaleString()} verified guest reviews</div>}
            </div>
          </div>
          {cats.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 max-w-lg">
              {cats.slice(0, 6).map((c, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs w-28 text-ink-soft truncate">{c.name}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-sand-200 overflow-hidden"><div className="h-full bg-ocean-500 rounded-full" style={{width:`${c.rating/10*100}%`}}/></div>
                  <span className="text-xs font-medium text-ink w-8 text-right">{c.rating?.toFixed?.(1)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {pros.length > 0 && (
        <div className="mt-6">
          <div className="text-xs text-ink-muted mb-2">People often mention</div>
          <div className="flex flex-wrap gap-2">{pros.slice(0, 8).map((p, i) => <span key={i} className="rounded-full bg-sunset-50 text-sunset-700 px-3 py-1 text-xs font-medium">"{p}"</span>)}</div>
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-2">
        {REVIEW_FILTERS.map(f => (
          <button key={f} onClick={() => { setFilter(f); setLimit(4); setExpanded(new Set()); }}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border ${filter === f ? "bg-ocean-600 border-ocean-600 text-white" : "bg-white border-ink/10 text-ink-soft hover:border-ink/25"}`}>{f}</button>
        ))}
      </div>
      {loading && <div className="mt-6 text-sm text-ink-muted">Loading reviews…</div>}
      {!loading && filtered.length === 0 && items.length > 0 && <p className="mt-6 text-sm text-ink-muted">No {filter.toLowerCase()} reviews yet.</p>}
      {!loading && filtered.length > 0 && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {filtered.slice(0, limit).map((r, i) => {
            const isExpanded = expanded.has(i);
            const cons = r.cons && !["nothing","n/a"].includes(r.cons.toLowerCase()) ? r.cons : "";
            const long = ((r.pros?.length || 0) + (cons?.length || 0)) > 200;
            return (
              <article key={i} className="card p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-ocean-100 text-ocean-800 grid place-items-center font-semibold">{r.name?.charAt(0)?.toUpperCase() || "?"}</div>
                    <div>
                      <div className="text-sm font-semibold text-ink">{r.name || "Anonymous"}</div>
                      <div className="text-[11px] text-ink-muted">{r.type?.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase()) || ""}{r.country ? ` · ${r.country.toUpperCase()}` : ""}{r.date ? ` · ${new Date(r.date).toLocaleDateString("en-US",{month:"short",year:"numeric"})}` : ""}</div>
                    </div>
                  </div>
                  {r.averageScore && <span className="rounded-lg text-white text-xs font-semibold px-2 py-1" style={{background: scoreColor(r.averageScore)}}>{r.averageScore}</span>}
                </div>
                {r.headline && <h4 className="mt-3 font-display text-base text-ink">{r.headline}</h4>}
                {r.pros && <p className={`mt-2 text-sm text-ink-soft ${!isExpanded && long ? "line-clamp-3" : ""}`}>{r.pros}</p>}
                {cons && <p className={`mt-2 text-sm text-sunset-700 ${!isExpanded && long ? "line-clamp-2" : ""}`}><span className="font-semibold">Note: </span>{cons}</p>}
                {long && <button onClick={() => setExpanded(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; })} className="mt-2 text-xs font-medium text-ocean-700">{isExpanded ? "Show less" : "Read more"}</button>}
              </article>
            );
          })}
        </div>
      )}
      {!loading && filtered.length > limit && <div className="mt-6 text-center"><button onClick={() => setLimit(x => x + 6)} className="btn-secondary">Show more reviews</button></div>}
    </section>
  );
}

/* ─── Amenities ──────────────────────────────────────────────────────────── */

function Amenities({ facilities }) {
  const [showAll, setShowAll] = useState(false);
  if (!facilities?.length) return null;
  const list = showAll ? facilities : facilities.slice(0, 12);
  return (
    <section>
      <h2 className="section-title">Amenities</h2>
      <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm text-ink-soft">
        {list.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckBadgeIcon size={16} className="text-ocean-600 mt-0.5 shrink-0"/><span>{typeof f === "string" ? f : f.name || ""}</span></li>)}
      </ul>
      {facilities.length > 12 && <button onClick={() => setShowAll(v => !v)} className="mt-4 btn-secondary text-sm">{showAll ? "Show less" : `Show all ${facilities.length} amenities`}</button>}
    </section>
  );
}

/* ─── PropertyPage ───────────────────────────────────────────────────────── */

export default function PropertyPage({ propertyId }) {
  const [property, setProperty] = useState(null);
  const [manifest, setManifest] = useState({});
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    let live = true;
    Promise.all([
      fetch(`/data/properties/${propertyId}.json`).then(r => r.ok ? r.json() : null),
      fetch("/data/image-health.json").then(r => r.ok ? r.json() : {}).catch(() => ({}))
    ]).then(([full, m]) => {
      if (!live) return;
      setProperty(full || null);
      setManifest(m || {});
      if (!full) setError("Property not found.");
    }).catch(() => setError("Could not load property."));
    return () => { live = false; };
  }, [propertyId]);

  // The JSON-LD lives in BodyWithSidebar, which is where the live rates are —
  // the entity carries the same price the page displays.
  useHead(property ? {
    title: `${property.name} — ${lodgingLabel(property.hotelTypeId)} in ${[property.area || property.city, islandDisplayFor(property)].filter(Boolean).join(", ")} | Aloha Rates`,
    description: clip(property.hotelDescription || property.description, 160) || `Book ${property.name}, a ${lodgingLabel(property.hotelTypeId).toLowerCase()} on ${islandDisplayFor(property)}, at real local rates with taxes and fees included.`,
    canonical: propertyPath(property)
  } : { title: "Property | Aloha Rates" });

  if (error) return <div className="container-page py-32 text-center" {...NAV.landing}><h1 className="section-title">{error}</h1><a href="/results/" className="btn-primary mt-6">Browse Hawaiʻi stays</a></div>;
  if (!property) return <div className="container-page py-32 text-center text-ink-muted" {...NAV.landing}><span {...NAV.loading}>Loading…</span></div>;

  const facilities = property.hotelFacilities || property.facilities || [];
  const facilityStrings = facilities.map(f => typeof f === "string" ? f : f.name).filter(Boolean);
  const stars = Math.min(5, Math.round(property.starRating || 0));
  const propertyTypeLabel = typeLabel(property.hotelTypeId);
  const islandSlug = slugify(property.island || "");
  const times = property.checkinCheckoutTimes || {};

  const highlights = [];
  if (property.hasBeachfront) highlights.push({ icon: PinIcon, label: "Beachfront" });
  if (property.hasPool) highlights.push({ icon: CheckBadgeIcon, label: "Pool" });
  if (property.hasWifi) highlights.push({ icon: CheckBadgeIcon, label: "Wi-Fi" });
  if (property.hasParking) highlights.push({ icon: CheckBadgeIcon, label: "Parking" });
  if (property.hasKitchen) highlights.push({ icon: CheckBadgeIcon, label: "Kitchen" });

  return (
    // Hotel Center landing page: the whole page is one Hotel + LodgingReservation
    // scope (identifier, name, stay dates, occupancy, one makesOffer).
    // No itemid here on purpose: matching the JSON-LD @id would merge the two
    // into one entity and Google would report every shared field (identifier,
    // name) as a duplicate. They stay separate — JSON-LD for Search, this
    // scope for Hotel Center's price crawler.
    <div className="container-page pt-6 pb-16" {...NAV.landing} itemScope itemType={lodgingMicrodataTypes()}>
      <meta itemProp="identifier" content={property.id}/>
      {/* Breadcrumb */}
      <nav className="text-sm text-ink-muted mb-4 flex flex-wrap items-center gap-1">
        <a href="/" className="hover:text-ocean-700">Home</a>
        <span>›</span>
        {property.island && <><a href={`/${islandSlug}/`} className="hover:text-ocean-700">{property.island}</a><span>›</span></>}
        {property.area && <span>{property.area}</span>}
      </nav>

      {/* Header */}
      <header className="mb-4">
        <h1 className="font-display font-semibold text-3xl md:text-5xl leading-tight text-ink" itemProp="name">{property.name}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {stars > 0 && <span className="text-sunset-500 tracking-wider">{"★".repeat(stars)}</span>}
          {property.rating > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="rounded-lg bg-ocean-700 text-white text-xs font-semibold px-2 py-1">{Number(property.rating).toFixed(1)}</span>
              <span className="text-ink font-medium">{ratingLabel(property.rating)}</span>
              {property.reviewCount > 0 && <span className="text-ink-muted">· {property.reviewCount.toLocaleString()} reviews</span>}
            </div>
          )}
          {/* Visible label matches the schema: "Hotel" or "Vacation rental" (+ supplier subtype). */}
          <span className="rounded-pill bg-sand-100 text-ink-soft px-3 py-1 text-xs font-medium">
            {propertyTypeLabel === "Hotels" || propertyTypeLabel === "Property" || propertyTypeLabel === "Not available"
              ? lodgingLabel(property.hotelTypeId)
              : `${lodgingLabel(property.hotelTypeId)} · ${propertyTypeLabel}`}
          </span>
        </div>
        {property.address && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
            <span className="text-ink-muted flex items-center gap-1.5"><PinIcon size={14}/>{property.address}</span>
            <button type="button" onClick={() => setMapOpen(true)} className="lg:hidden inline-flex items-center gap-1 text-ocean-700 font-medium hover:text-ocean-800">
              View on map <ChevronRightIcon size={14}/>
            </button>
          </div>
        )}
      </header>

      {/* Photo mosaic */}
      <PhotoMosaic propertyId={property.id} propertyName={property.name} manifest={manifest} onShowAll={i => setLightbox(i)}/>
      {lightbox !== null && <PhotoLightboxLoader propertyId={property.id} manifest={manifest} propertyName={property.name} index={lightbox} onClose={() => setLightbox(null)}/>}
      {mapOpen && <MapLightbox property={property} onClose={() => setMapOpen(false)}/>}

      <BodyWithSidebar property={property} highlights={highlights} facilityStrings={facilityStrings} times={times}/>
    </div>
  );
}

function BodyWithSidebar({ property, highlights, facilityStrings, times }) {
  const st = useBookingState({
    propertyId: property.id,
    rooms: property.rooms || [],
    propertyLat: property.location?.latitude ?? property.lat,
    propertyLng: property.location?.longitude ?? property.lng
  });

  // Search rich results: the Hotel / VacationRental entity, its breadcrumbs,
  // and — once rates land — the headline offer. The offer is built from the
  // same variant, the same total and the same breakdown the room card renders
  // in Microdata, so the two formats can never disagree about the price.
  const headline = st.rates?.[0];
  const chosen = headline ? st.variantFor(headline) : null;
  const offer = chosen ? (() => {
    const total = Math.round(chosen.baseRate + chosen.feesExcluded);
    const bd = priceBreakdown({ payToday: chosen.baseRate, feesIncluded: chosen.feesIncluded, taxesAndFees: chosen.taxesAndFees, total, currency: chosen.currency });
    return stayOfferNode({
      total, currency: bd.currency, lines: bd.lines, hotelName: property.name,
      checkinTime: stayDateTime(st.checkin, times?.checkin_start || times?.checkinStart),
      checkoutTime: stayDateTime(st.checkout, times?.checkout || times?.checkoutEnd, "11:00:00"),
      name: chosen.name, identifier: chosen.rateId || chosen.offerId,
      occupancy: st.adults + st.children.length,
      refundable: chosen.refundStatus === "refundable",
      cancelTime: cancelDateTime(chosen.cancelTime || chosen.cancelDate)
    });
  })() : undefined;

  useJsonLd("property-ldjson", [
    { "@context": CONTEXT, ...lodgingNode(property, { offer }) },
    breadcrumbNode([
      { name: "Home", path: "/" },
      ...(property.island ? [{ name: islandDisplayFor(property), path: `/${islandSlugFor(property)}/` }] : []),
      { name: property.name, path: propertyPath(property) }
    ])
  ]);

  return (
    <>
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-x-10 gap-y-10 items-start">
        <div className="min-w-0 space-y-12">
          {highlights.length > 0 && (
            <section>
              <div className="flex flex-wrap gap-2">
                {highlights.map(h => <span key={h.label} className="inline-flex items-center gap-2 rounded-pill bg-white ring-1 ring-ink/10 px-4 py-2 text-sm text-ink"><span className="text-ocean-600"><h.icon size={16}/></span> {h.label}</span>)}
              </div>
            </section>
          )}

          <AboutSection property={property}/>

          <RoomsBlock loading={st.loading} error={st.error} rates={st.rates} propertyId={property.id} propertyName={property.name}
            propertyArea={property.area || property.city} propertyIsland={property.island}
            checkin={st.checkin} checkout={st.checkout} adults={st.adults} children={st.children} stayNights={st.stayNights}
            cancelChoice={st.cancelChoice} setCancelChoice={st.setCancelChoice} extrasChoice={st.extrasChoice} setExtrasChoice={st.setExtrasChoice}
            variantFor={st.variantFor} bookingState={st} nearby={st.nearby} nearbyLoading={st.nearbyLoading} times={times}/>

          <Amenities facilities={facilityStrings}/>

          <Reviews hotelId={property.id} hotelName={property.name} rating={property.rating} reviewCount={property.reviewCount} sentimentAnalysis={property.sentiment_analysis}/>

          {(times.checkinStart || times.checkoutEnd) && (
            <section>
              <h2 className="section-title">Check-in &amp; policies</h2>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {times.checkinStart && (
                  <div className="card p-5">
                    <div className="text-xs text-ink-muted uppercase tracking-widest">Check-in</div>
                    <div className="mt-1 text-lg font-display text-ink">from {times.checkinStart}</div>
                    {times.checkinEnd && <div className="text-xs text-ink-muted">until {times.checkinEnd}</div>}
                  </div>
                )}
                {times.checkoutEnd && (
                  <div className="card p-5">
                    <div className="text-xs text-ink-muted uppercase tracking-widest">Check-out</div>
                    <div className="mt-1 text-lg font-display text-ink">by {times.checkoutEnd}</div>
                  </div>
                )}
              </div>
              {property.hotelImportantInformation && (
                <div className="mt-4 rounded-2xl bg-sand-100/70 p-5 text-sm text-ink-soft" dangerouslySetInnerHTML={{ __html: property.hotelImportantInformation }}/>
              )}
            </section>
          )}
        </div>

        <div className="hidden lg:block">
          <PropertyMap property={property}/>
        </div>
      </div>
    </>
  );
}

// Wrapper to trigger photo probing again inside lightbox (uses same manifest)
function PhotoLightboxLoader({ propertyId, manifest, propertyName, index, onClose }) {
  const { photos, probed } = usePhotoProbe(propertyId, manifest);
  if (!probed || photos.length === 0) return null;
  return <PhotoLightbox photos={photos} index={index} propertyName={propertyName} onClose={onClose}/>;
}
